/**
 * Remote-control key mapping. Arrows and Enter are handled by norigin (see initTvNavigation);
 * everything else (Back, media keys) goes through `resolveTvKey` + `useKeyMap`.
 *
 * Key codes: Android TV WebView reports Back as `Escape`/`GoBack` (the APK shell forwards
 * KEYCODE_BACK as a synthetic `keydown` with key `GoBack`, docs/08); webOS uses 461, Tizen 10009.
 * `Backspace` and `Escape` are the desktop-dev equivalents.
 */
export type TvKeyAction = 'back' | 'play' | 'pause' | 'playPause' | 'next' | 'prev' | 'stop';

export const TV_KEYS: Readonly<Record<TvKeyAction, readonly (string | number)[]>> = {
  back: ['Escape', 'Backspace', 'GoBack', 'BrowserBack', 'XF86Back', 27, 8, 461, 10009, 4],
  playPause: ['MediaPlayPause', 179, 85],
  play: ['MediaPlay', 415, 126],
  pause: ['MediaPause', 19, 127],
  next: ['MediaTrackNext', 176, 87],
  prev: ['MediaTrackPrevious', 177, 88],
  stop: ['MediaStop', 413, 86],
};

/** Keys norigin should treat as Enter besides the defaults (13 / 'Enter'). */
export const ENTER_KEYS: readonly (string | number)[] = [13, 'Enter', 'NumpadEnter', 'Select', 23];

function isEditable(target: EventTarget | null): boolean {
  if (!target || !(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable;
}

/** Maps a keyboard event to a TV action, or null when it is not one of ours. */
export function resolveTvKey(
  event: Pick<KeyboardEvent, 'key' | 'keyCode' | 'target'>,
): TvKeyAction | null {
  const key = event.key;
  const code = event.keyCode;
  for (const action of Object.keys(TV_KEYS) as TvKeyAction[]) {
    const codes = TV_KEYS[action];
    if (codes.includes(key) || (code !== 0 && codes.includes(code))) {
      // Backspace edits text when a real input has focus (desktop dev only).
      if (action === 'back' && (key === 'Backspace' || code === 8) && isEditable(event.target)) {
        return null;
      }
      return action;
    }
  }
  return null;
}
